import { makeUserFriendly } from '../utils'

import { UserFriendlyError } from './user-friendly-error'

/**
 * Enables type-checking that is not affected by having multiple versions of
 * this library installed
 */
// The string here is used as the type description for the type checker
const MARKER_SYMBOL = Symbol.for('@attain-apps/UserFriendlyAggregateError')

/**
 * User-friendly version of {@link AggregateError}
 *
 * Basically, combines a bunch of errors into one, which has one user-friendly
 * message explaining each error
 */
export class UserFriendlyAggregateError extends UserFriendlyError {
  // NOTE: must be all upper case to avoid naming collision with the class name
  public static override readonly NAME: string = 'UserFriendlyAggregateError'
  public override readonly name: string = UserFriendlyAggregateError.NAME

  // Used by TypeChecker
  private readonly [MARKER_SYMBOL] = true as const

  public get errors(): readonly Error[] {
    return this._errors
  }
  private readonly _errors: Error[]

  constructor(errors: readonly Error[] | AggregateError) {
    let errorsList: readonly Error[]
    let aggregateError: AggregateError
    if (errors instanceof AggregateError) {
      errorsList = errors.errors
      aggregateError = errors
    } else {
      errorsList = errors
      aggregateError = new AggregateError(errors)
    }
    const flattenedErrorList = _flattenErrorList(errorsList)
    super(
      `Encountered multiple issues:${flattenedErrorList
        .map((error, i) => _getUserFriendlyLineFor(error, i))
        .join('')}`,
      aggregateError,
    )
    this._errors = flattenedErrorList
  }

  public addError(error: Error): void {
    for (const individualError of _flattenErrorList([error])) {
      this._errors.push(individualError)
      this.message += _getUserFriendlyLineFor(
        individualError,
        this.errors.length - 1,
      )
    }
  }
}
export namespace UserFriendlyAggregateError {
  // NOTE: not using @attain-apps/type-guards as that would introduce circular imports
  export function isUserFriendlyAggregateError(
    val: unknown,
  ): val is UserFriendlyAggregateError {
    return !!(val as UserFriendlyAggregateError)?.[MARKER_SYMBOL]
  }
}

function _flattenErrorList(errorList: readonly Error[]): Error[] {
  const errorQueue = [...errorList]
  const flattenedErrorList: Error[] = []
  let error: Error | undefined
  while ((error = errorQueue.shift())) {
    if (
      error instanceof AggregateError ||
      UserFriendlyAggregateError.isUserFriendlyAggregateError(error)
    ) {
      errorQueue.unshift(...error.errors)
      continue
    }
    flattenedErrorList.push(error)
  }
  return flattenedErrorList
}

function _getUserFriendlyLineFor(error: Error, idx: number): string {
  return `\n(${idx + 1}) - ${makeUserFriendly(error).message}`
}
